/**
 * The metric picker: one collapsible block per metric pack, with select-all.
 *
 * Groups come from the backend catalog rather than a hardcoded list, so adding
 * a metric to the pack is a seed change and this file never learns about it.
 */

import { ChevronDown, ChevronRight, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import type { MetricDefinition, MetricGroup } from "../../types/kpiWorkflow";

interface MetricChecklistProps {
	groups: MetricGroup[];
	selected: ReadonlySet<string>;
	onToggleMetric: (key: string) => void;
	onToggleGroup: (keys: string[], nextSelected: boolean) => void;
}

interface MetricRowProps {
	metric: MetricDefinition;
	checked: boolean;
	onToggle: (key: string) => void;
}

const MetricRow: React.FC<MetricRowProps> = React.memo(
	({ metric, checked, onToggle }) => (
		<label className="flex items-start gap-2 py-1 cursor-pointer">
			<input
				type="checkbox"
				className="mt-0.5 rounded border-gray-300 text-[#008782] focus:ring-[#008782]"
				checked={checked}
				onChange={() => onToggle(metric.key)}
			/>
			<span className="text-base text-text-base">{metric.display_name}</span>
			{!metric.is_raw && (
				// Derived metrics are computed from the extracted ones, so ticking one
				// is not a request to find it in a document. Saying so here prevents
				// "why wasn't this in my file?" later.
				<span className="text-xs text-text-muted mt-0.5">calculated</span>
			)}
		</label>
	),
);
MetricRow.displayName = "MetricRow";

interface MetricGroupBlockProps {
	group: MetricGroup;
	selected: ReadonlySet<string>;
	onToggleMetric: (key: string) => void;
	onToggleGroup: (keys: string[], nextSelected: boolean) => void;
}

const MetricGroupBlock: React.FC<MetricGroupBlockProps> = ({
	group,
	selected,
	onToggleMetric,
	onToggleGroup,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const keys = useMemo(
		() => group.metrics.map((metric) => metric.key),
		[group.metrics],
	);
	const selectedCount = keys.filter((key) => selected.has(key)).length;
	const allSelected = selectedCount === keys.length && keys.length > 0;

	return (
		<div className="border border-gray-200 rounded-lg">
			<div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-lg">
				<button
					type="button"
					onClick={() => setIsOpen((open) => !open)}
					className="flex items-center gap-2 text-base font-medium text-text-base"
					aria-expanded={isOpen}
				>
					{isOpen ? (
						<ChevronDown className="w-4 h-4" />
					) : (
						<ChevronRight className="w-4 h-4" />
					)}
					{group.label ?? group.metric_group}
					<span className="text-xs text-text-muted">
						{selectedCount}/{keys.length}
					</span>
				</button>
				<button
					type="button"
					onClick={() => onToggleGroup(keys, !allSelected)}
					className="text-sm font-medium text-[#008782] hover:underline"
				>
					{allSelected ? "Clear all" : "Select all"}
				</button>
			</div>
			{isOpen && (
				<div className="px-3 py-2">
					{group.metrics.map((metric) => (
						<MetricRow
							key={metric.key}
							metric={metric}
							checked={selected.has(metric.key)}
							onToggle={onToggleMetric}
						/>
					))}
				</div>
			)}
		</div>
	);
};

/** Case- and punctuation-insensitive, so "loss to lease" finds "Loss to Lease". */
const normalize = (text: string): string =>
	text
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

/**
 * Narrow the catalog to metrics matching `query`, dropping groups left empty.
 *
 * A group whose NAME matches keeps all of its metrics: searching "occupancy"
 * should show the occupancy pack, not only the metrics carrying that word in
 * their own label.
 */
export function filterMetricGroups(
	groups: MetricGroup[],
	query: string,
): MetricGroup[] {
	const needle = normalize(query);
	if (!needle) return groups;
	const out: MetricGroup[] = [];
	for (const group of groups) {
		// The heading counts too: "leads" finds "Leads by Marketing Source",
		// whose rows are named just "ILS", "Walk-in"… (DOP-613).
		if (
			normalize(group.metric_group).includes(needle) ||
			normalize(group.label ?? "").includes(needle)
		) {
			out.push(group);
			continue;
		}
		const metrics = group.metrics.filter((metric) =>
			normalize(metric.display_name).includes(needle),
		);
		if (metrics.length > 0) out.push({ ...group, metrics });
	}
	return out;
}

const MetricChecklist: React.FC<MetricChecklistProps> = ({
	groups,
	selected,
	onToggleMetric,
	onToggleGroup,
}) => {
	const [query, setQuery] = useState("");
	const visible = useMemo(
		() => filterMetricGroups(groups, query),
		[groups, query],
	);
	const total = useMemo(
		() => groups.reduce((n, g) => n + g.metrics.length, 0),
		[groups],
	);

	if (groups.length === 0) {
		return (
			<p className="text-sm text-text-muted">
				No metrics are configured yet. Once the metric pack is seeded they
				appear here.
			</p>
		);
	}

	return (
		<div className="space-y-2">
			{/*
			 * The pack ships 124 metrics across nine groups and the only way
			 * through them was to scroll. Filtering is the difference between
			 * picking the four you came for and reading the catalogue.
			 */}
			<div className="relative">
				<Search
					className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-subtle"
					aria-hidden="true"
				/>
				<input
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={`Search ${total} metrics`}
					aria-label="Search metrics"
					className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-base text-text-base placeholder:text-text-subtle focus:border-primary-500 focus:ring-0"
				/>
			</div>

			{visible.length === 0 ? (
				<p className="px-1 py-3 text-sm text-text-muted">
					No metric matches “{query}”.
				</p>
			) : (
				visible.map((group) => (
					<MetricGroupBlock
						key={group.metric_group}
						group={group}
						selected={selected}
						onToggleMetric={onToggleMetric}
						onToggleGroup={onToggleGroup}
					/>
				))
			)}
		</div>
	);
};

export default MetricChecklist;
